import { PropsWithChildren, ReactNode } from "react";

import { Box, Flex, Heading, HStack, Skeleton, Spacer, VStack } from "@chakra-ui/react";

import { Actions } from "@/components/Actions";
import SandboxModeBanner from "@/components/SandboxModeBanner";
import HeaderTabs from "@/components/shared/HeaderTabs";
import TopBar from "@/components/shared/TopBar";
import { LayoutMode, layoutModes } from "@/constants/layoutModes";
import { TabItem } from "@/constants/tabs";
import { useLayoutSlots } from "@/providers/useLayoutSlots";
import { useWorkspace } from "@/providers/useWorkspace";

export type HeaderLayoutProps = {
  mode?: LayoutMode;
  hideSeparator?: boolean;
  heading?: ReactNode;
  subheading?: ReactNode;
  selectedTab?: TabItem;
  stateBadge?: ReactNode;
  hideBreadcrumbs?: boolean;
  breadcrumbsOmitIndexList?: ReadonlyArray<number>;
};

function HeaderLayout({
  mode = layoutModes.default,
  hideSeparator = false,
  heading = mode.header.heading,
  subheading = mode.header.subheading,
  selectedTab = mode.header.selectedTab,
  stateBadge = mode.header.stateBadge,
  breadcrumbsOmitIndexList = mode.header.breadcrumbsOmitIndexList,
  hideBreadcrumbs,
  children,
}: PropsWithChildren<HeaderLayoutProps>) {
  const { isSandbox, isManagedAgents } = useWorkspace();
  const { badges, actions } = useLayoutSlots();
  return (
    <Flex direction="column" h="full">
      {/* Managed Agents has its own trial banner (rendered by the agents surface);
          suppress the generic Release sandbox "Free Trial" banner for it. */}
      {isSandbox && !isManagedAgents && <SandboxModeBanner />}
      <VStack
        align="start"
        borderBottom={hideSeparator ? "none" : "1px"}
        borderColor="inherit"
        mt={{ base: 2, md: 4 }}
        px={{ base: 2, md: 6 }}
        spacing={1}
        w="full"
      >
        <TopBar
          breadcrumbsOmitIndexList={breadcrumbsOmitIndexList}
          height={10}
          hideBreadcrumbs={hideBreadcrumbs}
        />
        <Box mb={3} w="full">
          <HStack w="full">
            <Skeleton isLoaded={!!heading} w={heading ? "auto" : "30vh"}>
              <Heading as="h2" size={{ base: "heading4", lg: "heading3" }}>
                {heading}&nbsp;
              </Heading>
            </Skeleton>
            <HStack spacing={1}>
              {stateBadge}
              {badges}
            </HStack>
            <Spacer />
            {actions ? <Actions actions={actions} /> : mode.header.actions}
          </HStack>
          {subheading}
        </Box>
        {mode.header.tabs && (
          <HeaderTabs basePath={mode.header.basePath} selectedTab={selectedTab} tabs={mode.header.tabs} />
        )}
      </VStack>
      <Box flex={1} overflow="auto" tabIndex={0}>
        {children}
      </Box>
    </Flex>
  );
}

export default HeaderLayout;
